type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogEntry {
  level: LogLevel
  message: string
  timestamp: string
  context?: Record<string, any>
  error?: Error
  userId?: string
  sessionId?: string
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development'
  private logs: LogEntry[] = []
  private maxLogs = 1000

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    error?: Error
  ): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      error,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
    }
  }

  private getCurrentUserId(): string | undefined {
    // In a real app, get this from auth context
    try {
      const authData = localStorage.getItem('auth-storage')
      if (authData) {
        const parsed = JSON.parse(authData)
        return parsed.state?.user?.id
      }
    } catch {
      // Ignore errors when accessing localStorage
    }
    return undefined
  }

  private getSessionId(): string | undefined {
    try {
      let sessionId = sessionStorage.getItem('session-id')
      if (!sessionId) {
        sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        sessionStorage.setItem('session-id', sessionId)
      }
      return sessionId
    } catch {
      return undefined
    }
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isDevelopment) return true
    
    // In production, only log warnings and errors
    return level === 'warn' || level === 'error'
  }

  private formatConsoleOutput(entry: LogEntry): string {
    const { level, message, timestamp, context, userId } = entry
    const prefix = `[${timestamp}] ${level.toUpperCase()}`
    const userInfo = userId ? ` [User: ${userId}]` : ''
    const contextInfo = context ? ` Context: ${JSON.stringify(context)}` : ''
    
    return `${prefix}${userInfo}: ${message}${contextInfo}`
  }

  private addToInMemoryLogs(entry: LogEntry): void {
    this.logs.push(entry)
    
    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs)
    }
  }

  private sendToRemoteService(entry: LogEntry): void {
    // In production, send to monitoring service (e.g., CloudWatch, DataDog, etc.)
    if (!this.isDevelopment && entry.level === 'error') {
      // Example: send to external monitoring service
      try {
        fetch('/api/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entry)
        }).catch(() => {
          // Silently fail if logging service is down
        })
      } catch {
        // Silently fail
      }
    }
  }

  debug(message: string, context?: Record<string, any>): void {
    if (!this.shouldLog('debug')) return
    
    const entry = this.createLogEntry('debug', message, context)
    this.addToInMemoryLogs(entry)
    
    if (this.isDevelopment) {
      console.debug(this.formatConsoleOutput(entry))
    }
  }

  info(message: string, context?: Record<string, any>): void {
    if (!this.shouldLog('info')) return
    
    const entry = this.createLogEntry('info', message, context)
    this.addToInMemoryLogs(entry)
    
    if (this.isDevelopment) {
      console.info(this.formatConsoleOutput(entry))
    }
  }

  warn(message: string, context?: Record<string, any>): void {
    if (!this.shouldLog('warn')) return
    
    const entry = this.createLogEntry('warn', message, context)
    this.addToInMemoryLogs(entry)
    
    console.warn(this.formatConsoleOutput(entry))
    this.sendToRemoteService(entry)
  }

  error(message: string, error?: Error, context?: Record<string, any>): void {
    const entry = this.createLogEntry('error', message, context, error)
    this.addToInMemoryLogs(entry)
    
    console.error(this.formatConsoleOutput(entry))
    if (error) {
      console.error('Error details:', error)
    }
    
    this.sendToRemoteService(entry)
  }

  // Performance logging
  time(label: string): void {
    if (this.isDevelopment) {
      console.time(label)
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      console.timeEnd(label)
    }
  }

  // User action logging
  logUserAction(action: string, details?: Record<string, any>): void {
    this.info(`User action: ${action}`, { 
      action, 
      ...details,
      userAgent: navigator.userAgent,
      url: window.location.href,
    })
  }

  // API call logging
  logApiCall(method: string, url: string, status?: number, duration?: number): void {
    const level = status && status >= 400 ? 'warn' : 'info'
    const message = `API ${method} ${url} - ${status || 'pending'}`
    
    this[level](message, {
      method,
      url,
      status,
      duration,
    })
  }

  // Get logs for debugging
  getLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logs.filter(log => log.level === level)
    }
    return [...this.logs]
  }

  // Clear logs
  clearLogs(): void {
    this.logs = []
  }

  // Export logs for support
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2)
  }
}

// Create singleton instance
export const logger = new Logger()

// Helper functions for common use cases
export const logError = (message: string, error?: Error, context?: Record<string, any>) => {
  logger.error(message, error, context)
}

export const logUserAction = (action: string, details?: Record<string, any>) => {
  logger.logUserAction(action, details)
}

export const logApiCall = (method: string, url: string, status?: number, duration?: number) => {
  logger.logApiCall(method, url, status, duration)
}

// Performance measurement decorator
export function measurePerformance(target: any, propertyName: string, descriptor: PropertyDescriptor) {
  const method = descriptor.value

  descriptor.value = function (...args: any[]) {
    const label = `${target.constructor.name}.${propertyName}`
    logger.time(label)
    
    try {
      const result = method.apply(this, args)
      
      if (result instanceof Promise) {
        return result.finally(() => logger.timeEnd(label))
      } else {
        logger.timeEnd(label)
        return result
      }
    } catch (error) {
      logger.timeEnd(label)
      throw error
    }
  }

  return descriptor
} 